import { useEffect } from 'react';

interface ExtensionDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ExtensionDownloadModal({ isOpen, onClose }: ExtensionDownloadModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleQuickBooksDownload = () => {
    // Navigate to XeroVerifier installation page
    window.REACT_APP_NAVIGATE('/extension-install-chrome-XeroVerifier');
    onClose();
  };

  const handleXeroDownload = () => {
    // Navigate to XEROVerifier installation page
    window.REACT_APP_NAVIGATE('/extension-install-chrome-XEROVerifier');
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-4xl w-full"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-8 py-6 relative"
          style={{ backgroundColor: '#29B3E6' }}
        >
          <h2 className="text-3xl font-bold text-white">
            Choose Your Accounting Platform
          </h2>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white transition-colors cursor-pointer w-8 h-8 flex items-center justify-center rounded"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <p className="text-lg text-gray-600 text-center mb-8">
            Select your accounting platform to download the XeroVerifier extension
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Xero Card */}
            <div 
              className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
              onClick={handleXeroDownload}
            >
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/319c6627ef46a869fe60403415ecdbf0.png"
                    alt="Xero Logo"
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Xero</h3>
                <p className="text-gray-600 mb-6">
                  Install XeroVerifier extension for Xero to add verification stamps and protect your business invoices.
                </p>
                <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                  <i className="ri-download-line text-xl"></i>
                  <span className="font-semibold">Install Extension</span>
                </div>
              </div>
            </div>

            {/* QuickBooks Online Card */}
            <div 
              className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
              onClick={handleQuickBooksDownload}
            >
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/7e135ab53f3a89b1b2baef520522ad77.png"
                    alt="QuickBooks Online Logo"
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">QuickBooks Online</h3>
                <p className="text-gray-600 mb-6">
                  Install XeroVerifier extension for QuickBooks Online to secure your invoices with verification stamps.
                </p>
                <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                  <i className="ri-download-line text-xl"></i>
                  <span className="font-semibold">Install Extension</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-6 bg-gray-50 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <i className="ri-information-line text-green-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Installation Requirements</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Chrome browser (version 88 or higher)</li>
                  <li>• Active QuickBooks Online or Xero subscription</li>
                  <li>• Administrator access to your accounting platform</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}